import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import ConfirmDialog from "components/ConfirmDialog";
import LinkParentCompanyModal from "components/Companies/LinkParentCompanyModal";
import { Network, Link2, Unlink, Building2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Internal-only Parent Company control on the Company Detail Overview tab.
 * Shows the current parent (if any) with an Unlink button, or a Link to
 * Parent button that opens the picker modal.
 */
export default function ParentCompanyCard({ company, refetch }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [parent, setParent] = useState(null);
  const [loadingParent, setLoadingParent] = useState(false);
  const [children, setChildren] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  // Fetch the parent company when this company has one.
  const fetchParent = useCallback(async () => {
    if (!company?.parentCompanyId) {
      setParent(null);
      return;
    }
    if (!auth?.user || !effectiveCompanyId) return;
    setLoadingParent(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getCompany(company.parentCompanyId),
        auth.user,
        effectiveCompanyId
      );
      setParent(res?.data || null);
    } catch (err) {
      console.error("[ParentCompanyCard] Fetch parent failed", err);
      setParent(null);
    } finally {
      setLoadingParent(false);
    }
  }, [company?.parentCompanyId, auth?.user, effectiveCompanyId]);

  // Fetch children to disable "Link to Parent" if this company is itself a parent
  // (server enforces the 2-level depth rule; we hide the button too for UX).
  const fetchChildren = useCallback(async () => {
    if (!company?.companyId || !auth?.user || !effectiveCompanyId) return;
    if (company.parentCompanyId) {
      setChildren([]);
      return;
    }
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getCompanyChildren(company.companyId),
        auth.user,
        effectiveCompanyId
      );
      setChildren(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setChildren([]);
    }
  }, [company?.companyId, company?.parentCompanyId, auth?.user, effectiveCompanyId]);

  useEffect(() => {
    fetchParent();
  }, [fetchParent]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const handleUnlink = async () => {
    setUnlinking(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.updateCompany(company.companyId),
        auth.user,
        effectiveCompanyId,
        { parentCompanyId: null }
      );
      toast({
        status: "success",
        title: "Parent unlinked",
        description: `${company.name} is no longer a child company.`,
      });
      setConfirmUnlink(false);
      refetch();
    } catch (err) {
      console.error("[ParentCompanyCard] Unlink failed", err);
      toast({
        status: "destructive",
        title: "Failed to unlink parent",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setUnlinking(false);
    }
  };

  const isNotClient = company.companyType && company.companyType !== "client";
  const hasChildren = children.length > 0;

  return (
    <div className="surface">
      <div className="px-6 py-4 border-b border-border flex items-center space-x-3">
        <div className="p-2 bg-brand/20 rounded-lg">
          <Network className="w-5 h-5 text-brand" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">
          Parent Company
        </h2>
      </div>
      <div className="p-6 space-y-3">
        {company.parentCompanyId ? (
          <>
            <p className="text-sm text-muted-foreground">
              This company is a child of its parent. Users of the parent
              company can switch into this company.
            </p>
            <div className="flex items-center justify-between gap-3 p-4 rounded-lg border border-border bg-muted/40">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-md bg-brand/20 shrink-0">
                  <Building2 className="h-4 w-4 text-brand" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">
                    {loadingParent
                      ? "Loading..."
                      : parent?.name || company.parentCompanyId}
                  </div>
                  <div className="text-xs text-muted-foreground truncate font-mono">
                    {company.parentCompanyId}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {parent?.companyId && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate(`/companies/${parent.companyId}`)}
                    title="View parent"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setConfirmUnlink(true)}
                >
                  <Unlink className="h-4 w-4" />
                  Unlink
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Link this company to a parent so that the parent's users can
              switch into it and see a consolidated portfolio.
            </p>
            {isNotClient ? (
              <p className="text-xs text-muted-foreground">
                Only client companies can be linked to a parent.
              </p>
            ) : hasChildren ? (
              <p className="text-xs text-muted-foreground">
                This company already has {children.length}{" "}
                {children.length === 1 ? "child" : "children"} — it cannot
                itself become a child (2-level depth limit).
              </p>
            ) : (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setPickerOpen(true)}
              >
                <Link2 className="h-4 w-4" />
                Link to Parent
              </button>
            )}
          </>
        )}
      </div>

      {pickerOpen && (
        <LinkParentCompanyModal
          child={company}
          onClose={() => setPickerOpen(false)}
          onLinked={() => {
            setPickerOpen(false);
            refetch();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmUnlink}
        title="Unlink parent company?"
        description={
          parent?.name
            ? `${company.name} will no longer be a child of ${parent.name}. The parent's users will lose access to this company's data.`
            : `${company.name} will no longer be a child of its parent. The parent's users will lose access to this company's data.`
        }
        confirmLabel={unlinking ? "Unlinking..." : "Unlink"}
        variant="destructive"
        onConfirm={handleUnlink}
        onCancel={() => !unlinking && setConfirmUnlink(false)}
      />
    </div>
  );
}
